/**
 * WorkspaceDashboard
 *
 * Compact-theme entry point for managing workspaces without picking a workspace id first.
 * Surfaces create workspace, recent activity, grouped workspace directory, and workflows.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LoadingSpinner,
  WorkspaceList,
} from '@tw/tw-component-library';
import { Button } from '@/ui';
import {
  FiArrowLeft,
  // FiBriefcase,
  // FiGitBranch,
  // FiLayers,
  // FiPlusCircle,
} from 'react-icons/fi';
import { CompactVisualPromptsSection } from '@/components/visualPrompts/compact';
import { CompactWorkspaceDirectoryList } from './CompactWorkspaceDirectoryList';
import { CompactRecentActivityList } from './CompactRecentActivityList';

import { CreateWorkspace } from '@/components/container/createWorkspace/CreateWorkspace';
import {
  CollapsibleSectionsStack,
  PageContainer,
  WorkspaceOverviewHero,
  WorkspaceOverviewSubtitle,
  WorkspaceOverviewTitle,
} from '@/components/pages/workspacePage/WorkSpacePage.styles';
import { WorkspaceCollapsibleSection } from '@/components/pages/workspacePage/WorkspaceCollapsibleSection';
import {
  DashboardGrid,
  DashboardToolbar,
  IntroActions,
  TwoColumn,
} from './WorkspaceDashboard.styles';
import { UseAuth } from '@/contexts/AuthContext';
import { useWorkspace, WorkspaceItem } from '@/contexts/WorkspaceContext';
import { postUserAuditLogWorkspace } from '@/apis/core/UserAuditLog/post/postAuditLogWorkspace';
import { useCompactTheme } from '@/hooks/useCompactTheme';
import { COMPACT_VISUAL_PROMPT_MESSAGES } from '@/constants/visualPrompts/compactMessages';
import { useWorkspaceDashboardSections } from './useWorkspaceDashboardSections';

const CompactWorkspaceList = styled(WorkspaceList)`
  border: 1px solid ${({ theme }) => theme.colors.border.light} !important;
  border-radius: ${({ theme }) => theme.borderRadius.sm} !important;
  padding: 1rem !important;

  > div:first-child,
  > button:first-child {
    margin: -1rem -1rem 0.75rem -1rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
    padding: 0.7rem 1rem;
  }

  h2 {
    font-size: 1rem;
    font-weight: 600;
    text-align: left;
  }
`;

type RawWorkflow = {
  id?: string;
  workflowId?: string;
  name?: string;
  title?: string;
};

const buildWorkflowListItems = (
  workspaces: { id: string; title: string; workflows: unknown[] }[],
) => {
  const rows: { id: string; title: string; lastUsed: string }[] = [];
  for (const ws of workspaces) {
    const list = Array.isArray(ws.workflows) ? ws.workflows : [];
    for (const w of list) {
      const wf = w as RawWorkflow;
      const wfId = wf.id ?? wf.workflowId;
      const label = wf.name ?? wf.title ?? 'Workflow';
      if (!wfId) continue;
      rows.push({
        id: `${ws.id}|${wfId}`,
        title: label,
        lastUsed: ws.title,
      });
    }
  }
  return rows;
};

const VISUAL_PROMPTS_SECTION_SELECTOR =
  '[data-tour="workspace-dashboard-visual-prompts-section"]';

export const WorkspaceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessToken } = UseAuth();
  const {
    workspaceItemsWithPresence,
    workspacesOrderedByLastUpdate,
    setCurrentWorkspaceId,
    workspaces,
    isLoading,
  } = useWorkspace();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { isCompactTheme } = useCompactTheme();
  const { sections, setSectionExpanded } = useWorkspaceDashboardSections();
  const [workspaceRecentActivity, setWorkspaceRecentActivity] = useState<
    Record<
      string,
      {
        action?: string;
        entityType?: string;
        timestamp?: string;
      }
    >
  >({});

  const recentItemsWithAvatars = useMemo(() => {
    const itemById = new Map(
      workspaceItemsWithPresence.flatMap((g) =>
        g.items.map((item) => [item.id, item] as const),
      ),
    );
    return workspacesOrderedByLastUpdate.map((ws) => {
      const item = itemById.get(ws.id);
      return item
        ? { ...item, lastUsed: ws.time }
        : {
            id: ws.id,
            title: ws.title,
            lastUsed: ws.time,
            workspaceType: ws.workspaceType,
          };
    });
  }, [workspaceItemsWithPresence, workspacesOrderedByLastUpdate]);

  const recentWorkspaceItems = useMemo(() => {
    return recentItemsWithAvatars.map((item) => {
      const recent = workspaceRecentActivity[item.id];
      if (!recent) return item;
      return {
        ...item,
        recentActivityAction: recent.action,
        recentActivityEntityType: recent.entityType,
        recentActivityTimestamp: recent.timestamp,
      };
    });
  }, [recentItemsWithAvatars, workspaceRecentActivity]);

  const workflowRows = useMemo(
    () =>
      buildWorkflowListItems(
        workspaces.map((w) => ({
          id: w.id,
          title: w.title,
          workflows: w.workflows ?? [],
        })),
      ),
    [workspaces],
  );

  const compactPromptWorkspaces = useMemo(
    () =>
      workspacesOrderedByLastUpdate.map((ws) => ({
        id: ws.id,
        title: ws.title,
      })),
    [workspacesOrderedByLastUpdate],
  );

  useEffect(() => {
    setCurrentWorkspaceId(null);
  }, [setCurrentWorkspaceId]);

  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string } | null)?.scrollTo;
    if (scrollTo !== 'visualPrompts' || isLoading || !isCompactTheme) return;

    setSectionExpanded('visualPrompts', true);

    const timer = window.setTimeout(() => {
      const section = document.querySelector(VISUAL_PROMPTS_SECTION_SELECTOR);
      section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);

    return () => window.clearTimeout(timer);
  }, [location.state, isLoading, isCompactTheme, setSectionExpanded]);

  useEffect(() => {
    if (isCompactTheme || recentItemsWithAvatars.length === 0) return;

    let isCancelled = false;
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    const workspaceIds = recentItemsWithAvatars.slice(0, 8).map((i) => i.id);

    const fetchRecentActivity = async () => {
      const requests = workspaceIds.map(async (workspaceId) => {
        try {
          const response = await postUserAuditLogWorkspace({
            getAccessToken,
            workspaceId,
            body: {
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
              results: '10',
              offset: '0',
              users: [],
            },
          });
          const responses = Array.isArray(response) ? response : [response];
          const logs = responses.flatMap((res) => res.logs ?? []);
          if (!logs.length) return [workspaceId, null] as const;

          logs.sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          );
          const latest = logs[0];
          return [
            workspaceId,
            {
              action: latest.action,
              entityType: latest.entityType,
              timestamp: new Date(latest.date).toLocaleString(),
            },
          ] as const;
        } catch {
          return [workspaceId, null] as const;
        }
      });

      const results = await Promise.all(requests);
      if (isCancelled) return;

      setWorkspaceRecentActivity((prev) => {
        const next = { ...prev };
        results.forEach(([workspaceId, latest]) => {
          if (latest) {
            next[workspaceId] = latest;
          }
        });
        return next;
      });
    };

    void fetchRecentActivity();

    return () => {
      isCancelled = true;
    };
  }, [isCompactTheme, recentItemsWithAvatars, getAccessToken]);

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleWorkspaceClick = useCallback(
    (workspaceId: string) => {
      navigate(`/workspace/${workspaceId}`);
    },
    [navigate],
  );

  const handleWorkflowRowClick = useCallback(
    (item: WorkspaceItem) => {
      const [workspaceId] = item.id.split('|');
      if (workspaceId) {
        navigate(`/workspace/${workspaceId}/documents`);
      }
    },
    [navigate],
  );

  const handleDirectoryCreateAskSession = useCallback(
    (item: WorkspaceItem) => {
      navigate(`/workspace/${item.id}/ask-session`);
    },
    [navigate],
  );

  const handleDirectoryUploadDocument = useCallback(
    (item: WorkspaceItem) => {
      navigate(`/workspace/${item.id}/documents?upload=1`);
    },
    [navigate],
  );

  const handleDirectoryRunTask = useCallback(
    (item: WorkspaceItem) => {
      navigate(`/workspace/${item.id}/prompt-library`);
    },
    [navigate],
  );

  const ListComponent = isCompactTheme ? CompactWorkspaceList : WorkspaceList;

  if (isLoading) {
    return <LoadingSpinner variant="fullPage" />;
  }

  return (
    <PageContainer>
      <DashboardToolbar>
        <Button
          type="button"
          variant="ghost"
          onClick={handleBack}
          aria-label="Back to home"
        >
          <FiArrowLeft style={{ marginRight: '0.35rem' }} />
          Back to home
        </Button>
      </DashboardToolbar>

      <DashboardGrid>
        <WorkspaceOverviewHero>
          {/* TODO Not sure if i like these or not, not sure if it's just stating the obvious. */}
          {/* <WorkspaceOverviewEyebrow>
            <FiBriefcase />
            Workspace hub
          </WorkspaceOverviewEyebrow> */}
          <WorkspaceOverviewTitle>Workspace dashboard</WorkspaceOverviewTitle>
          <WorkspaceOverviewSubtitle>
            Create workspaces for matters and teams, jump back into recent
            activity, browse everything you belong to, and open workflows from
            the workspaces that use them.
          </WorkspaceOverviewSubtitle>
        </WorkspaceOverviewHero>

        <CollapsibleSectionsStack $isCompact={isCompactTheme}>
          <WorkspaceCollapsibleSection
            title="Start a new workspace"
            hint="A workspace is a shared hub for documents, Ask conversations, prompt libraries, and activity for a client or matter. Create one for each engagement so collaborators stay aligned and AI has the right context."
            expanded={sections.create}
            onExpandedChange={(expanded) =>
              setSectionExpanded('create', expanded)
            }
            data-tour="workspace-dashboard-create-section"
          >
            <IntroActions>
              <Button type="button" onClick={() => setIsCreateModalOpen(true)}>
                Create new workspace
              </Button>
            </IntroActions>
          </WorkspaceCollapsibleSection>

          {isCompactTheme && (
            <WorkspaceCollapsibleSection
              title={COMPACT_VISUAL_PROMPT_MESSAGES.eyebrow}
              hint={COMPACT_VISUAL_PROMPT_MESSAGES.sectionDescription}
              expanded={sections.visualPrompts}
              onExpandedChange={(expanded) =>
                setSectionExpanded('visualPrompts', expanded)
              }
              data-tour="workspace-dashboard-visual-prompts-section"
            >
              <CompactVisualPromptsSection
                getAccessToken={getAccessToken}
                workspaces={compactPromptWorkspaces}
                onCreateWorkspace={() => setIsCreateModalOpen(true)}
              />
            </WorkspaceCollapsibleSection>
          )}

          <TwoColumn>
            <WorkspaceCollapsibleSection
              title={
                isCompactTheme
                  ? 'Recent activity · Last 30 days'
                  : 'Recent activity'
              }
              hint={
                isCompactTheme
                  ? 'Your latest actions across workspaces, ask sessions, and documents. Filter by action or type; use pages to load more from your audit history.'
                  : 'Workspaces you have used lately, with the latest audit signal when available.'
              }
              expanded={sections.recentActivity}
              onExpandedChange={(expanded) =>
                setSectionExpanded('recentActivity', expanded)
              }
              data-tour="workspace-dashboard-recent-activity-section"
            >
              {isCompactTheme ? (
                <CompactRecentActivityList
                  title="Recent activity"
                  onNavigate={(route) => navigate(route)}
                />
              ) : (
                <ListComponent
                  {...{
                    title: 'View recent',
                    variant: 'activity',
                    enablePagination: true,
                    itemsPerPage: 6,
                    enableSearch: true,
                    searchPlaceholder: 'Search recent workspaces...',
                    items: recentWorkspaceItems,
                    onItemClick: (item: WorkspaceItem) =>
                      handleWorkspaceClick(item.id),
                    showMemberPresence: false,
                  }}
                />
              )}
            </WorkspaceCollapsibleSection>

            <WorkspaceCollapsibleSection
              title="Your directory"
              hint="Private, collaborative, and standard workspaces you belong to. Use tabs to filter by type; search across all of them."
              expanded={sections.directory}
              onExpandedChange={(expanded) =>
                setSectionExpanded('directory', expanded)
              }
              data-tour="workspace-dashboard-directory-section"
            >
              {isCompactTheme ? (
                <CompactWorkspaceDirectoryList
                  title="All workspaces"
                  groups={
                    workspaceItemsWithPresence.length > 0
                      ? workspaceItemsWithPresence
                      : []
                  }
                  listLessTab="Private"
                  enableSearch
                  enablePagination
                  enableTabs
                  itemsPerPage={6}
                  searchPlaceholder="Search workspaces..."
                  onItemClick={(item) => handleWorkspaceClick(item.id)}
                  onCreateAskSession={handleDirectoryCreateAskSession}
                  onUploadDocument={handleDirectoryUploadDocument}
                  onRunTask={handleDirectoryRunTask}
                />
              ) : (
                <ListComponent
                  {...{
                    title: 'All workspaces',
                    groups:
                      workspaceItemsWithPresence.length > 0
                        ? workspaceItemsWithPresence
                        : [],
                    listLessTab: 'Private',
                    enableSearch: true,
                    titleAsButton: false,
                    enablePagination: true,
                    enableTabs: true,
                    itemsPerPage: 6,
                    searchPlaceholder: 'Search workspaces...',
                    onCreateNew: () => setIsCreateModalOpen(true),
                    onItemClick: (item: WorkspaceItem) =>
                      handleWorkspaceClick(item.id),
                    showMemberPresence: false,
                  }}
                />
              )}
            </WorkspaceCollapsibleSection>
          </TwoColumn>

          <WorkspaceCollapsibleSection
            title="Workflows across workspaces"
            hint="Workflows live on a workspace. Pick one to open its documents area where workflow-aware tools are available."
            expanded={sections.workflows}
            onExpandedChange={(expanded) =>
              setSectionExpanded('workflows', expanded)
            }
            data-tour="workspace-dashboard-workflows-section"
          >
            {workflowRows.length === 0 ? (
              <p>No workflows are attached to your workspaces yet.</p>
            ) : (
              <ListComponent
                {...{
                  title: 'Workflows',
                  items: workflowRows,
                  enableSearch: true,
                  searchPlaceholder: 'Search workflows...',
                  enablePagination: true,
                  itemsPerPage: 8,
                  onItemClick: handleWorkflowRowClick,
                }}
              />
            )}
          </WorkspaceCollapsibleSection>
        </CollapsibleSectionsStack>
      </DashboardGrid>

      <CreateWorkspace
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </PageContainer>
  );
};
